import React, { useState } from "react";
import { useFetch } from "@/hooks/useApi";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Loader, StatusPill, Plate, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { gbp } from "@/lib/format";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", phone: "", email: "", driver_id: "", status: "active", assigned_van_reg: "", rate_multiplier: "1.0", min_guarantee: "0" };

export default function Drivers() {
  const { data: drivers, loading, refetch } = useFetch("/drivers");
  const { data: vans } = useFetch("/vans");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (d) => { setForm({ ...d, rate_multiplier: String(d.rate_multiplier), min_guarantee: String(d.min_guarantee ?? 0) }); setEditId(d.id); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, rate_multiplier: parseFloat(form.rate_multiplier), min_guarantee: parseFloat(form.min_guarantee || 0) };
      if (editId) await api.put(`/drivers/${editId}`, payload);
      else await api.post("/drivers", payload);
      toast.success(editId ? "Driver updated" : "Driver created");
      setOpen(false);
      refetch();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this driver?")) return;
    try { await api.delete(`/drivers/${id}`); toast.success("Deleted"); refetch(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <Loader />;
  const filtered = (drivers || []).filter((d) => d.name.toLowerCase().includes(q.toLowerCase()) || d.driver_id.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="fp-fade-in">
      <PageHeader title="Drivers" subtitle={`${drivers?.length || 0} driver profiles`} testId="drivers-page">
        <div className="relative w-full sm:w-56">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search drivers" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-full" data-testid="drivers-search" />
        </div>
        <Button onClick={openNew} className="w-full sm:w-auto" data-testid="add-driver-button"><Icons.Plus className="h-4 w-4 mr-1.5" /> Add Driver</Button>
      </PageHeader>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead>
                <TableHead>Driver ID</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Van</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Min/day</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((d) => (
                <TableRow key={d.id} data-testid={`driver-row-${d.driver_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-primary/15 text-primary grid place-items-center text-xs font-bold uppercase">{d.name.slice(0, 2)}</div>
                      <div>
                        <p className="font-medium">{d.name}</p>
                        <p className="text-xs text-muted-foreground">{d.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{d.driver_id}</TableCell>
                  <TableCell className="font-mono text-sm">{d.phone || "—"}</TableCell>
                  <TableCell>{d.assigned_van_reg ? <Plate reg={d.assigned_van_reg} /> : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="font-mono font-semibold text-primary">×{d.rate_multiplier}</TableCell>
                  <TableCell className="font-mono">{d.min_guarantee ? gbp(d.min_guarantee) : "—"}</TableCell>
                  <TableCell><StatusPill status={d.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(d)} data-testid={`edit-driver-${d.driver_id}`}><Icons.Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(d.id)} data-testid={`delete-driver-${d.driver_id}`}><Icons.Trash2 className="h-4 w-4 text-rose-400" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {filtered.length === 0 && <EmptyState icon="Users" title="No drivers found" subtitle="Add your first driver to get started." />}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="driver-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Driver" : "New Driver"}</DialogTitle>
            <DialogDescription>Set the driver's details, rate multiplier and optional minimum guaranteed daily income.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Name</Label><Input value={form.name} onChange={set("name")} className="mt-1.5" data-testid="driver-name-input" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5" data-testid="driver-phone-input" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={set("email")} className="mt-1.5" data-testid="driver-email-input" /></div>
            <div><Label>Driver ID</Label><Input value={form.driver_id} onChange={set("driver_id")} placeholder="Auto if blank" className="mt-1.5" /></div>
            <div>
              <Label>Assigned Van</Label>
              <Select value={form.assigned_van_reg || "none"} onValueChange={(v) => set("assigned_van_reg")(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5" data-testid="driver-van-select"><SelectValue placeholder="Select van" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {(vans || []).map((v) => <SelectItem key={v.id} value={v.registration}>{v.registration} · {v.make_model}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rate Multiplier</Label>
              <Input type="number" step="0.1" value={form.rate_multiplier} onChange={set("rate_multiplier")} className="mt-1.5" data-testid="driver-rate-input" />
            </div>
            <div>
              <Label>Min Guaranteed £/day</Label>
              <Input type="number" step="1" value={form.min_guarantee} onChange={set("min_guarantee")} className="mt-1.5" data-testid="driver-guarantee-input" placeholder="0 = none" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-driver-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
